import { useId } from 'react';

/**
 * Marque Repère — deux hexagones arrondis qui se chevauchent sans se confondre.
 *
 * La forme dit ce que le produit fait : deux foyers distincts, une zone commune, et
 * personne devant l'autre. Le décalage est diagonal et non concentrique — deux formes
 * emboîtées auraient dessiné une hiérarchie, or le produit tout entier repose sur
 * l'absence de hiérarchie entre les parents (docs/01 §5.3).
 *
 * **Le détachement est découpé, pas peint.** La silhouette élargie de l'hexagone avant est
 * retirée de l'hexagone arrière par un masque : l'écart entre les deux formes est un trou,
 * pas un liseré. Un liseré aurait figé la couleur du fond dans le dessin — juste sur le
 * bandeau marine, entouré d'un halo bleu partout ailleurs. Ici la marque tient sur
 * n'importe quel fond.
 *
 * Le symbole est en SVG inline plutôt qu'en `<img>` : il hérite ainsi des variables
 * `--logo-*` posées par le contexte, et ne provoque ni requête ni saut de mise en page.
 */
export function Logo({ taille = 28 }: { taille?: number }) {
  // Deux logos sur une même page — en-tête et pied — partageraient sinon un identifiant de
  // masque, et le second effacerait la découpe du premier. Les deux-points que `useId`
  // produit sont retirés : `url(#:r0:)` n'est pas honoré partout.
  const masque = `logo-decoupe-${useId().replace(/:/g, '')}`;

  return (
    <svg width={taille} height={taille} viewBox="0 0 110 110" aria-hidden="true" focusable="false">
      <mask id={masque} maskUnits="userSpaceOnUse" x="0" y="0" width="110" height="110">
        <rect width="110" height="110" fill="#FFFFFF" />
        <path d={HEXAGONE_AVANT_ELARGI} fill="#000000" />
      </mask>
      <path d={HEXAGONE_ARRIERE} fill="var(--logo-arriere, #A6C6AC)" mask={`url(#${masque})`} />
      <path d={HEXAGONE_AVANT} fill="var(--logo-avant, #FFFFFF)" />
    </svg>
  );
}

/**
 * Hexagones pointe en haut, coins largement adoucis. La géométrie est relevée sur le
 * fichier de marque (`assets/brand/logo-repere.png`) : centres (37,5 ; 45) et
 * (72 ; 64), rayon 35, arrondi 13. `AVANT_ELARGI` est le même hexagone à 38,5 de rayon —
 * les 3,5 d'écart sont la largeur du vide entre les deux formes.
 */
const HEXAGONE_AVANT =
  'M26.2 16.5 Q37.5 10.0 48.8 16.5 L56.6 21.0 Q67.8 27.5 67.8 40.5 L67.8 49.5 ' +
  'Q67.8 62.5 56.6 69.0 L48.8 73.5 Q37.5 80.0 26.2 73.5 L18.4 69.0 Q7.2 62.5 7.2 49.5 ' +
  'L7.2 40.5 Q7.2 27.5 18.4 21.0 Z';

const HEXAGONE_AVANT_ELARGI =
  'M25.1 13.7 Q37.5 6.5 49.9 13.6 L58.5 18.6 Q70.8 25.8 70.8 40.1 L70.8 50.0 ' +
  'Q70.8 64.2 58.5 71.4 L49.9 76.3 Q37.5 83.5 25.1 76.3 L16.5 71.4 Q4.2 64.2 4.2 50.0 ' +
  'L4.2 40.0 Q4.2 25.7 16.5 18.6 Z';

const HEXAGONE_ARRIERE =
  'M60.7 35.5 Q72.0 29.0 83.3 35.5 L91.1 40.0 Q102.3 46.5 102.3 59.5 L102.3 68.5 ' +
  'Q102.3 81.5 91.1 88.0 L83.3 92.5 Q72.0 99.0 60.7 92.5 L52.9 88.0 Q41.7 81.5 41.7 68.5 ' +
  'L41.7 59.5 Q41.7 46.5 52.9 40.0 Z';
